<template>
  <div id="two-factor-authentication">
    <div>
      <h1 class="init-block--heading">{{ t('global.two_factor_authentication') }}</h1>
      <div class="subheader u-mb3">{{ t('global.select_the_mfa_method') }}</div>
    </div>
    <el-radio-group v-model="selectedMethod">
      <div v-if="authenticatorAppActive">
        <el-radio class="u-mt3" label="authenticator-app" :checked='selectedMethod === "authenticator-app"'
         v-model="selectedMethod">
          {{ t('global.authenticator_app') }}
        </el-radio>
      </div>
      <div v-if="emailOptions.length">
        <el-radio class="u-mt3" label="email" :checked='selectedMethod === "email"'
          v-model="selectedMethod">
          {{ t('global.email') }}
        </el-radio>
        <div class="u-pl4">
          <el-select v-if="selectedMethod==='email' && emailOptions.length > 1" v-model="selectedMethodId">
            <el-option
              v-for="(email, i) in emailOptions"
              :key="email.value + i"
              :label="email.label"
              :value="email.value">
            </el-option>
          </el-select>
          <el-button v-if="selectedMethod === 'email'" type="text" @click="sendCode" :disabled="disabledSendCodeButtons" class="remove-background">{{ t('global.send_code') }}</el-button>
        </div>
      </div>
      <div v-if="phones.length">
        <el-radio class="u-mt3" label="sms" :checked='selectedMethod === "sms"'
          v-model="selectedMethod">
          {{ t('global.sms') }}
        </el-radio>
        <div class="u-pl4">
          <el-select v-if="selectedMethod==='sms' && phoneOptions.length > 1" v-model="selectedMethodId">
            <el-option
              v-for="phone in phoneOptions"
              :key="phone.value"
              :label="phone.label"
              :value="phone.value">
            </el-option>
          </el-select>
          <el-button v-if="selectedMethod === 'sms'" type="text" @click="sendCode" :disabled="disabledSendCodeButtons" class="remove-background">{{ t('global.send_code') }}</el-button>
        </div>
      </div>
    </el-radio-group>
    <el-form class="mx-auto form-container">
      <div v-if="!mfaCodeError" class="subheader u-mb3">{{ t('global.enter_the_code_generated') }}</div>
      <el-form-item>
        <el-input v-model="code"
                  :class="{ 'u-input-error': mfaCodeError }"
                  @keyup.enter.native="performVerification"
                  :disabled="blockVerifyProcess"></el-input>
        <div v-if="mfaCodeError" class="u-input-validation u-danger u-pt2 u-pb1">
          {{ mfaCodeError }}
        </div>
      </el-form-item>
    </el-form>
    <el-button data-cy="button__sign-in" type="primary" class="verify-button el-button--wide u-text-uppercase"
      @click="performVerification" v-loading="loading" :disabled="blockVerifyProcess">
      {{ t('global.verify') }}
    </el-button>
  </div>
</template>

<script>

import {mapActions} from 'vuex'

export default {
  name: 'two-factor-authentication',
  props: {
    loginResponse: {
      type: Object,
      required: true
    },
    loading: {
      type: Boolean,
      default: false
    },
  },
  computed: {
    disabledSendCodeButtons () {
      return this.disabledSendCode || this.blockVerifyProcess
    },
    emails: function () {
      if (!this.loginResponse.emails) {
        return []
      }
      return [...this.loginResponse.emails]
    },
    phones: function () {
      if (!this.loginResponse.phones) {
        return []
      }
      return this.loginResponse.phones
    },
    authenticatorAppActive: function () {
      if (!this.loginResponse.authenticator_enabled) {
        this.selectedMethod = 'email'
      }
      return !!this.loginResponse.authenticator_enabled
    },
    awaitingMfaToken: function () {
      return this.loginResponse.awaiting_mfa_token
    },
    smsDisabled: function () {
      if (!this.phones) {
        return true
      }
      return this.phones.length === 0
    },
    emailOptions: function () {
      return this.emails.map(email => {
        return {
          value: email.id,
          label: email.email
        }
      })
    },
    phoneOptions: function () {
      return this.phones.map(phone => {
        return {
          value: phone.id,
          label: phone.phone
        }
      })
    }
  },
  data () {
    return {
      selectedMethod: 'authenticator-app',
      selectedMethodId: null,
      shouldShowPhonesSelect: false,
      shouldShowEmailsSelect: false,
      disabledSendCode: true,
      code: '',
      mfaCodeError: '',
      blockVerifyProcess: false  // if too many failed MFA
    }
  },
  methods: {
    ...mapActions('common', ['savePreferredLanguage']),
    ...mapActions('login', [
      'verifyMfa',
      'sendMfaCode'
    ]),
    async performVerification () {
      this.mfaCodeError = ''
      this.$emit('on-set-busy')

      try {
        await this.verifyMfa({
          mfa_code: this.code,
          token: this.loginResponse.awaiting_mfa_token,
          mfa_code_type: this.selectedMethod === 'authenticator-app' ? 'totp' : 'hotp'
        })
        this.$emit('login-success')
      } catch (e) {
        this.$emit('on-set-idle')
        if (e.data.cause === 'account.mfa_attempts_exceeded') {
          this.$elAlert(
            this.t('account.mfa_attempts_exceeded'),
            this.t('global.error')
          )
          this.blockVerifyProcess = true
        }
        this.mfaCodeError = this.t(e.data.cause || 'account.email_confirmation_code_does_not_match')
      }
    },
    async sendCode () {
      // this function called when you want to send code via SMS or E-Mail
      if (this.disabledSendCodeButtons) {
        return
      }
      const submittedType = this.selectedMethod === 'email' ? 'emails' : 'phones'
      const payload = {
        submitted_type: submittedType,
        token: this.loginResponse.awaiting_mfa_token,
        submitted_id: this.selectedMethodId
      }

      let recipientType = this.selectedMethod === 'email' ? this.t('global.email') : this.t('global.sms')
      let recipient = (submittedType === 'phones' ? this.phoneOptions : this.emailOptions)
        .find(obj => { return obj.value === this.selectedMethodId }).label
      try {
        await this.sendMfaCode(payload)
        this.$elAlert(
          this.t('account.mfa_code_sent_successfully', {recipient_type: recipientType, recipient: recipient}),
          this.t('global.done')
        )
      } catch (e) {
        this.$elAlert(
          this.t('global.code_sending_error'),
          this.t('global.error'),
        )
      }
    },
  },
  watch: {
    selectedMethod: function (val) {
      if (val === 'authenticator-app') {
        this.selectedMethodId = null
      }
      if (val === 'email') {
        if (this.emails && this.emails.length === 1) {
          this.selectedMethodId = this.emails[0].id
        } else {
          this.selectedMethodId = null
          this.shouldShowEmailsSelect = true
        }
      }
      if (val === 'sms') {
        if (this.phones && this.phones.length === 1) {
          this.selectedMethodId = this.phones[0].id
        } else {
          this.selectedMethodId = null
          this.shouldShowPhonesSelect = true
        }
      }
    },
    selectedMethodId: function (val) {
      this.disabledSendCode = !val
    }
  }
}
</script>

<style lang="scss" scoped>
.show-password-sign {
  position: absolute;
  right: 15px;
  top: 31px;
  z-index: 100;
}

.visible-password-dash {
  &:before {
    content: '';
    border: 1px solid #898a89;
    width: 33px;
    display: inline-block;
    transform: rotate(135deg);
    position: relative;
    left: 30px;
    bottom: 7px;
  }
}
.form-container {
  margin-top: 1rem;
  margin-bottom: 1rem;
}

.verify-button {
  margin-bottom: 1rem;
}

.remove-background {
  background-color: transparent !important;
  background: none !important;
  padding-bottom: 0;
}
</style>
